import React, { useEffect } from 'react';
import {
    createBrowserRouter,
    Navigate,
    RouterProvider as Router,
    useParams,
} from 'react-router-dom';
import useChatStore from '../stores/useChatStore';
import { TRouteConfig, TUiConfig } from '@core/shared/types';
import { ErrorPage } from '@core/pages/ErrorPage/ErrorPage';

/**
 * Component responsible for handling route changes and updating the chat state.
 * It listens for changes in the `threadId` and updates the relevant chat configuration.
 *
 * @param {object} props - The component props
 * @param {TRouteConfig} props.routeConfig - Configuration for the current route, including the page to be rendered
 * @returns {React.ReactNode} The component for the current route based on the configuration.
 */
const RouteHandler: React.FC<{ routeConfig: TRouteConfig }> = ({ routeConfig }) => {
    const { setRouteConfig, setThreadId, isPreview } = useChatStore();
    const { id: threadId } = useParams<{ id?: string }>();

    useEffect(() => {
        // Update route config and threadId whenever they change
        !isPreview && setRouteConfig(routeConfig);
        setThreadId(threadId ?? null); // If threadId is not available, set it to null
    }, [threadId, setThreadId, setRouteConfig, routeConfig]);

    return <routeConfig.page />; // Render the page associated with the current route
};

/**
 * RouterProvider component which configures routes based on UI configuration.
 * It dynamically creates a router based on provided routes, and redirects to the default route.
 *
 * @param {object} props - The component props
 * @param {TUiConfig} props.uiConfig - UI configuration that contains route configurations and template
 * @returns {React.ReactNode} The RouterProvider which handles route rendering and navigation
 */
const RouterProvider: React.FC<{ uiConfig: TUiConfig }> = ({ uiConfig }) => {
    const { routeConfig } = uiConfig;

    // Dynamically generate the routes from the provided routeConfig
    const routes = routeConfig.map((routeConfigData) => ({
        path: `/${routeConfigData.routePath}/:id?`, // Define dynamic path for each route
        element: <RouteHandler routeConfig={routeConfigData} />,
    }));

    // Find the default route path from the routeConfig
    const defaultRoutePath =
        routeConfig.find((config) => config.index)?.routePath || routeConfig[0]?.routePath;

    if (!defaultRoutePath) {
        console.warn(
            'No default route for UI found, set index to true for one of the routes. Fallback to first route.',
        );
    }

    // Create the router with a default redirect and dynamic routes
    const router = createBrowserRouter([
        {
            path: '*',
            element: <Navigate to="/" />, // Redirect any unmatched routes to the root
        },
        {
            path: '/',
            element: <uiConfig.template />, // Use the provided template for the root
            errorElement: <ErrorPage />, // Add this line
            children: [
                {
                    index: true, // Default route
                    element: <Navigate to={defaultRoutePath} />, // Redirect to the default route path
                },
                ...routes, // Add dynamically generated routes
            ],
        },
    ]);

    return <Router router={router} />; // Return the Router component to manage the routing logic
};

export default RouterProvider;
